import React, { useState, useEffect, useRef } from 'react';
import Toast from '@/components/common/Toast';
import {
  fetchGetPersonalCertification,
  fetchSubmitPersonalCertification,
} from '@/service/api';
import {
  CertStatus,
  IndFormData,
  IndividualCertForm,
  CertStatusResult,
} from '../components/certification';

const certStatusConfig: Record<CertStatus, { label: string; color: string; icon: string; desc: string }> = {
  unverified: {
    label: '未认证',
    color: 'bg-foreground-100 text-foreground-600',
    icon: 'ri-error-warning-line',
    desc: '',
  },
  pending: {
    label: '审核中',
    color: 'bg-accent-100 text-accent-700',
    icon: 'ri-time-line',
    desc: '您的个人实名认证申请正在审核中',
  },
  approved: {
    label: '已认证',
    color: 'bg-green-100 text-green-700',
    icon: 'ri-check-double-line',
    desc: '个人实名认证已通过',
  },
  rejected: {
    label: '已驳回',
    color: 'bg-red-100 text-red-600',
    icon: 'ri-close-circle-line',
    desc: '认证未通过，请根据驳回原因修改后重新提交',
  },
};

const mapApiStatusToCertStatus = (status?: string): CertStatus => {
  if (!status) return 'unverified';
  const s = status.toUpperCase();
  if (s === 'PENDING' || s === 'AUDITING' || s === 'SUBMITTED') return 'pending';
  if (s === 'APPROVED' || s === 'PASSED' || s === 'SUCCESS') return 'approved';
  if (s === 'REJECTED' || s === 'FAILED') return 'rejected';
  return 'unverified';
};

export interface PersonalCertSectionProps {
  className?: string;
  /** 是否已被其他认证锁定（如企业认证已通过/审核中） */
  isLockedByOther?: boolean;
  /** 外部控制显隐或展开表单 */
  showForm?: boolean;
  /** 展开表单状态改变回调 */
  onShowFormChange?: (show: boolean) => void;
  /** 认证状态更新回调 */
  onStatusChange?: (status: CertStatus) => void;
}

export const PersonalCertSection: React.FC<PersonalCertSectionProps> = ({
  className = '',
  isLockedByOther = false,
  showForm: externalShowForm,
  onShowFormChange,
  onStatusChange,
}) => {
  const [status, setStatus] = useState<CertStatus>('unverified');
  const [rejectReason, setRejectReason] = useState<string>('');
  const [submittedAt, setSubmittedAt] = useState<string>('');
  const [auditedAt, setAuditedAt] = useState<string>('');
  const [internalShowForm, setInternalShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const fetchedRef = useRef(false);

  const showForm = externalShowForm !== undefined ? externalShowForm : internalShowForm;
  const setShowForm = (val: boolean) => {
    setInternalShowForm(val);
    onShowFormChange?.(val);
  };

  const [indForm, setIndForm] = useState<IndFormData>({
    realName: '',
    idNumber: '',
    contactName: '',
    contactPhone: '',
    idFrontAssetId: '',
    idBackAssetId: '',
    handheldIdAssetId: '',
  });

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    fetchGetPersonalCertification()
      .then((res: any) => {
        const raw = res?.data?.data || res?.data || res;
        if (raw && (raw.code === undefined || raw.code === 200 || raw.code === 0 || raw.certification_status || raw.certificationStatus)) {
          const st = mapApiStatusToCertStatus(raw.certification_status || raw.certificationStatus);
          setStatus(st);
          onStatusChange?.(st);

          if (raw.submitted_at || raw.submittedAt) setSubmittedAt(raw.submitted_at || raw.submittedAt || '');
          if (raw.audited_at || raw.auditedAt) setAuditedAt(raw.audited_at || raw.auditedAt || '');

          if (raw.audit_remark || raw.auditRemark) {
            setRejectReason(raw.audit_remark || raw.auditRemark || '');
          }

          const frontAsset = raw.id_front_asset || raw.idFrontAsset;
          const backAsset = raw.id_back_asset || raw.idBackAsset;
          const realName = raw.real_name || raw.realName || '';
          const idNo = raw.id_no || raw.idNo || raw.id_number || raw.idNumber || '';
          const frontId = raw.id_front_asset_id || raw.idFrontAssetId || frontAsset?.asset_id;
          const backId = raw.id_back_asset_id || raw.idBackAssetId || backAsset?.asset_id;

          if (realName || idNo || frontAsset || backAsset || frontId || backId) {
            setIndForm((prev) => ({
              ...prev,
              realName: realName || prev.realName,
              idNumber: idNo || prev.idNumber,
              idType: (raw.id_type || raw.idType as Api.PersonalCertification.IdType) || prev.idType || 'ID_CARD',
              contactName: raw.contact_name || raw.contactName || realName || prev.contactName,
              contactPhone: raw.contact_phone || raw.contactPhone || prev.contactPhone,
              idFrontAssetId: frontId || prev.idFrontAssetId,
              idBackAssetId: backId || prev.idBackAssetId,
              idFrontAsset: frontAsset || prev.idFrontAsset,
              idBackAsset: backAsset || prev.idBackAsset,
            }));
          }
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData: Api.PersonalCertification.SubmitPersonalCertificationRequest = {
        real_name: indForm.realName,
        id_no: indForm.idNumber,
        id_type: indForm.idType || 'ID_CARD',
        contact_name: indForm.contactName || indForm.realName,
        contact_phone: indForm.contactPhone,
        id_front_asset_id: indForm.idFrontAssetId || undefined,
        id_back_asset_id: indForm.idBackAssetId || undefined,
      };

      const res: any = await fetchSubmitPersonalCertification(submitData);
      const code = res?.code;
      const isSuccess = res && (code === undefined || code === 200 || code === 0 || code === '200' || code === '0');
      if (isSuccess) {
        const newStatus = mapApiStatusToCertStatus(res.certification_status || 'PENDING');
        setStatus(newStatus);
        onStatusChange?.(newStatus);
        setShowForm(false);
        const msg = res.message || res.msg || '个人实名认证申请提交成功！';
        setToast({ message: msg, type: 'success' });
      } else {
        const errMsg = res?.message || res?.msg || '提交个人实名认证失败';
        setToast({ message: errMsg, type: 'error' });
      }
    } catch (err: any) {
      const errMsg = err?.message || err?.msg || '提交个人实名认证失败，请稍后重试';
      setToast({ message: errMsg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const statusConfig = certStatusConfig[status];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 模块标题栏 */}
      <div className="flex items-center justify-between pb-4 border-b border-foreground-100">
        <div>
          <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
            <i className="ri-user-vcard-line text-accent-500 text-lg"></i>
            个人实名认证
          </h2>
          <p className="text-xs text-foreground-500 mt-1">验证个人真实身份信息，保障账号安全与合法权益</p>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusConfig.color}`}>
          {statusConfig.label}
        </span>
      </div>

      {/* 当未展开表单且为未认证或已驳回状态时展示功能概览与入口 */}
      {!showForm && (status === 'unverified' || status === 'rejected') && (
        <div className="p-5 rounded-xl bg-foreground-50/60 border border-foreground-100 space-y-4">
          {status === 'rejected' && rejectReason && (
            <div className="p-3 rounded-lg bg-red-50 text-red-600 text-xs flex items-start gap-2 border border-red-100">
              <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
              <div>
                <span className="font-medium">驳回原因：</span>
                {rejectReason}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-foreground-600">
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-white border border-foreground-100/80 shadow-xs">
              <div className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-sm"></i>
              </div>
              <div>
                <div className="font-semibold text-foreground-900 mb-0.5">身份真实保障</div>
                <div className="text-foreground-500 text-[11px]">校验真实姓名与身份证件号</div>
              </div>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-white border border-foreground-100/80 shadow-xs">
              <div className="w-7 h-7 rounded-md bg-accent-50 text-accent-600 flex items-center justify-center shrink-0">
                <i className="ri-wallet-3-line text-sm"></i>
              </div>
              <div>
                <div className="font-semibold text-foreground-900 mb-0.5">解锁结算权益</div>
                <div className="text-foreground-500 text-[11px]">支持个人创作者收益提现</div>
              </div>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-white border border-foreground-100/80 shadow-xs">
              <div className="w-7 h-7 rounded-md bg-green-50 text-green-600 flex items-center justify-center shrink-0">
                <i className="ri-medal-line text-sm"></i>
              </div>
              <div>
                <div className="font-semibold text-foreground-900 mb-0.5">实名认证标识</div>
                <div className="text-foreground-500 text-[11px]">提升账号公信力与合作机会</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-foreground-100/60">
            <span className="text-xs text-foreground-500">需准备：本人真实身份信息及联系方式</span>
            <button
              type="button"
              disabled={isLockedByOther}
              onClick={() => !isLockedByOther && setShowForm(true)}
              className={`px-5 py-2 text-xs font-medium rounded-lg transition-colors shrink-0 ${
                isLockedByOther
                  ? 'bg-foreground-100 text-foreground-400 cursor-not-allowed'
                  : 'bg-accent-500 hover:bg-accent-600 text-white shadow-xs'
              }`}
            >
              {status === 'rejected' ? '重新提交认证' : '开始实名认证'}
            </button>
          </div>
        </div>
      )}

      {/* 个人认证表单 */}
      {showForm && (status === 'unverified' || status === 'rejected') && (
        <IndividualCertForm
          individualStatus={status}
          rejectReason={rejectReason}
          formData={indForm}
          submitting={submitting}
          onChange={(data) => setIndForm((prev) => ({ ...prev, ...data }))}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* 审核中或已通过状态展示 */}
      {(status === 'pending' || status === 'approved') && (
        <CertStatusResult
          activeCert="individual"
          individualStatus={status}
          enterpriseStatus="unverified"
          indForm={indForm}
          entForm={{
            companyName: '',
            creditCode: '',
            legalPerson: '',
            legalId: '',
            contactName: '',
            contactPhone: '',
            businessLicenseAssetId: '',
            authorizationAssetId: '',
          }}
          submittedAt={submittedAt}
          auditedAt={auditedAt}
        />
      )}

      {/* Toast 提示 */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default PersonalCertSection;
